import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, type ShopChoice } from "../lib/auth";
import { Button, ErrorBanner, Field, Input, Select } from "../components/ui";

export function LoginPage() {
  const { user, signIn, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shops, setShops] = useState<ShopChoice[] | null>(null);
  const [membershipId, setMembershipId] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn(email, password, membershipId || undefined);
      // One person can belong to several shops, so the shop is chosen at
      // sign-in rather than guessed (build guide / 01).
      if (result.needsShopChoice) {
        setShops(result.memberships);
        setMembershipId(result.memberships[0]?.id ?? "");
      }
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-ink">Auto Shop</h1>
          <p className="mt-1 text-sm text-slate-500">
            Customers, jobs, invoices and payments in one place.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-lg border border-slate-200 bg-white p-6"
        >
          <ErrorBanner error={error} />

          <Field label="Email" required>
            <Input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </Field>

          <Field label="Password" required>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Field>

          {shops && (
            <Field label="Shop" hint="Your account is attached to more than one business.">
              <Select value={membershipId} onChange={(e) => setMembershipId(e.target.value)}>
                {shops.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.organizationName} — {s.locationName} ({s.role.toLowerCase().replace("_", " ")})
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Button type="submit" loading={submitting} className="w-full">
            {shops ? "Continue" : "Sign in"}
          </Button>
        </form>

        <details className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm">
          <summary className="cursor-pointer font-medium text-ink">Demo accounts</summary>
          <p className="mt-2 text-slate-600">
            Password for all: <code className="font-mono">AutoShop2026!</code>
          </p>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            <li>
              <code>owner@bayouauto.test</code> — everything
            </li>
            <li>
              <code>advisor@bayouauto.test</code> — no cost or margin data
            </li>
            <li>
              <code>tech@bayouauto.test</code> — assigned jobs only
            </li>
            <li>
              <code>books@bayouauto.test</code> — ledger and reconciliation
            </li>
            <li>
              <code>consultant@bayouauto.test</code> — belongs to two shops
            </li>
          </ul>
        </details>
      </div>
    </div>
  );
}
